import { useAxiosConfig } from '@shared/api/AxiosConfigProvider';
import {
  getSoldGoodsFoodListQueryKey,
  getSoldGoodsPackagingListQueryKey,
  getSoldGoodsUncategorisedListQueryKey,
  useSoldGoodsBulkPartialUpdate,
} from '@shared/api/lib/sold-goods/sold-goods';
import { useQueryClient } from '@tanstack/react-query';

type BulkMutationOptions = NonNullable<
  Parameters<typeof useSoldGoodsBulkPartialUpdate>[0]
>['mutation'];

/**
 * Unified bulk action on sold goods. Sold-goods sibling of
 * `useBulkUpdatePurchasedGoods` — the action travels inside the payload and
 * each action carries only its own fields. Mutation options are passed through
 * per call site (change-type invalidates lists; food-review uses optimistic
 * resolution).
 */
export const useBulkUpdateSoldGoods = (mutation?: BulkMutationOptions) =>
  useSoldGoodsBulkPartialUpdate({
    request: useAxiosConfig(),
    mutation,
  });

/**
 * Invalidate every list a change-type action can move an item between
 * (Excluded items, Food & drink, Packaging). Sold-goods sibling of
 * `useInvalidatePurchasedGoodsLists`.
 */
export const useInvalidateSoldGoodsLists = () => {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries(getSoldGoodsUncategorisedListQueryKey()),
      queryClient.invalidateQueries(getSoldGoodsFoodListQueryKey()),
      queryClient.invalidateQueries(getSoldGoodsPackagingListQueryKey()),
    ]);
};
